/** Data access for the assets feature. */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import {
  compactParams,
  type ListState,
  type Paged,
  type PageOf,
  usePagedQuery,
  usePageQuery,
} from "@/shared/lib/paging";

/** Asset row/detail. */
export type Asset = Schemas["AssetOut"];
/** Asset tree node. */
export type AssetTreeNode = Schemas["AssetTreeNode"];
/** Permission-scoped operational context for one component. */
export type ComponentHealth = Schemas["ComponentHealthOut"];

/** One batched request for the host's component context, never a request per card. */
export function useComponentHealth(assetId: string): UseQueryResult<ComponentHealth[]> {
  return useQuery({
    queryKey: ["assets", assetId, "component-health"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/fleet/assets/{asset_id}/component-health", {
          params: { path: { asset_id: assetId } },
        }),
      ),
    enabled: Boolean(assetId),
  });
}
/** Certificate. */
export type Certificate = Schemas["CertificateOut"];
/** Work order summary for the asset's open list. */
export type WorkOrder = Schemas["WorkOrderOut"];
/** Create payload. */
export type AssetIn = Schemas["AssetIn"];
/** Edit payload. */
export type AssetPatch = Schemas["AssetPatch"];
/** Asset type with schema. */
export type AssetType = Schemas["AssetTypeOut"];
/** Certificate payload. */
export type CertificateIn = Schemas["CertificateIn"];
/** Position payload. */
export type PositionIn = Schemas["PositionIn"];
/** Install payload. */
export type InstallIn = Schemas["InstallIn"];
/** Remove payload. */
export type RemoveIn = Schemas["RemoveIn"];

/** Filters accepted by the asset list. */
export interface AssetFilters {
  q?: string;
  status?: string;
  vessels_only?: boolean;
  type_key?: string;
  site_id?: string;
  criticality?: string;
  ownership?: "owned" | "external";
}

/** GeoJSON produced by `/assets/map`. */
export interface AssetFeatureCollection {
  type: "FeatureCollection";
  features: {
    type: "Feature";
    id: string;
    geometry: { type: "Point"; coordinates: [number, number] };
    properties: {
      code: string;
      name: string;
      type_key: string;
      status: string;
      criticality: string;
      position_at: string | null;
      site: string | null;
    };
  }[];
}

/** Paged asset list. */
export function useAssets(filters: AssetFilters): Paged<Asset> {
  return usePagedQuery(["assets", filters], async (cursor) =>
    unwrap(
      await api.GET("/api/v1/assets/", {
        params: { query: compactParams({ ...filters, cursor, limit: 50 }) },
      }),
    ),
  );
}

/** Sorted, numbered page of assets for the register table. */
export function useAssetPage(filters: AssetFilters, state: ListState): PageOf<Asset> {
  return usePageQuery(["assets", "page", filters], state, async (params) =>
    unwrap(
      await api.GET("/api/v1/assets/", {
        params: { query: compactParams({ ...filters, ...params }) },
      }),
    ),
  );
}

/** Single asset. */
export function useAsset(assetId: string): UseQueryResult<Asset> {
  return useQuery({
    queryKey: ["assets", assetId],
    staleTime: 30_000,
    enabled: Boolean(assetId),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/assets/{asset_id}", { params: { path: { asset_id: assetId } } }),
      ),
  });
}

/** Component tree for an asset. */
export function useAssetTree(assetId: string): UseQueryResult<AssetTreeNode> {
  return useQuery({
    queryKey: ["assets", assetId, "tree"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/assets/{asset_id}/tree", {
          params: { path: { asset_id: assetId } },
        }),
      ),
  });
}

/** Certificates for an asset. */
export function useAssetCertificates(
  assetId: string,
  enabled = true,
): UseQueryResult<Certificate[]> {
  return useQuery({
    queryKey: ["assets", assetId, "certificates"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/assets/certificates", {
          params: { query: { asset_id: assetId, limit: 100 } },
        }),
      ).items,
    enabled,
  });
}

/** Open work orders for an asset. */
export function useAssetWorkOrders(assetId: string, enabled = true): UseQueryResult<WorkOrder[]> {
  return useQuery({
    queryKey: ["work-orders", { asset_id: assetId, open_only: true }],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/work-orders/", {
          params: { query: { asset_id: assetId, open_only: true, limit: 50 } },
        }),
      ).items,
    enabled,
  });
}

/** Fleet map features. */
export function useAssetMap(
  vesselsOnly = true,
  ownership?: "owned" | "external",
): UseQueryResult<AssetFeatureCollection> {
  return useQuery({
    queryKey: ["assets", "map", vesselsOnly, ownership],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/assets/map", {
          params: { query: { vessels_only: vesselsOnly, ownership } },
        }),
      ) as unknown as AssetFeatureCollection,
    refetchInterval: 60_000,
  });
}

/** Change asset status. */
export function useChangeAssetStatus(
  assetId: string,
): UseMutationResult<Asset, Error, { status: string; reason: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(
        await api.POST("/api/v1/assets/{asset_id}/status", {
          params: { path: { asset_id: assetId } },
          body,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["assets"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/** Asset status values in display order. */
export const ASSET_STATUSES = [
  "planned",
  "active",
  "standby",
  "under_maintenance",
  "out_of_service",
  "disposed",
] as const;

/** Asset types (cached; the attribute schema lives on each type). */
export function useAssetTypes(): UseQueryResult<AssetType[]> {
  return useQuery({
    queryKey: ["asset-types"],
    queryFn: async () => unwrap(await api.GET("/api/v1/assets/types")),
    staleTime: 5 * 60_000,
  });
}

function invalidateAsset(qc: ReturnType<typeof useQueryClient>, id?: string): void {
  void qc.invalidateQueries({ queryKey: ["assets"] });
  void qc.invalidateQueries({ queryKey: ["dashboard"] });
  if (id) {
    void qc.invalidateQueries({ queryKey: ["assets", id] });
  }
}

/** Create an asset. */
export function useCreateAsset(): UseMutationResult<Asset, Error, AssetIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => unwrap(await api.POST("/api/v1/assets/", { body })),
    onSuccess: () => invalidateAsset(qc),
  });
}

/** Edit an asset. */
export function useUpdateAsset(assetId: string): UseMutationResult<Asset, Error, AssetPatch> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(
        await api.PATCH("/api/v1/assets/{asset_id}", {
          params: { path: { asset_id: assetId } },
          body,
        }),
      ),
    onSuccess: () => invalidateAsset(qc, assetId),
  });
}

/** Install this asset onto a parent. */
export function useInstallAsset(assetId: string): UseMutationResult<unknown, Error, InstallIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(
        await api.POST("/api/v1/assets/{asset_id}/install", {
          params: { path: { asset_id: assetId } },
          body,
        }),
      ),
    onSuccess: () => invalidateAsset(qc, assetId),
  });
}

/** Remove this asset from its parent. */
export function useRemoveAsset(assetId: string): UseMutationResult<unknown, Error, RemoveIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(
        await api.POST("/api/v1/assets/{asset_id}/remove", {
          params: { path: { asset_id: assetId } },
          body,
        }),
      ),
    onSuccess: () => invalidateAsset(qc, assetId),
  });
}

/** Add a certificate. */
export function useCreateCertificate(
  assetId: string,
): UseMutationResult<Certificate, Error, CertificateIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(
        await api.POST("/api/v1/assets/{asset_id}/certificates", {
          params: { path: { asset_id: assetId } },
          body,
        }),
      ),
    onSuccess: () => invalidateAsset(qc, assetId),
  });
}

/** Replace a certificate. */
export function useUpdateCertificate(
  assetId: string,
): UseMutationResult<Certificate, Error, { id: string; body: CertificateIn }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      unwrap(
        await api.PUT("/api/v1/assets/certificates/{certificate_id}", {
          params: { path: { certificate_id: id } },
          body,
        }),
      ),
    onSuccess: () => invalidateAsset(qc, assetId),
  });
}

/** Revoke a certificate while retaining its metadata and pinned document history. */
export function useRevokeCertificate(
  assetId: string,
): UseMutationResult<Certificate, Error, { id: string; reason: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }) =>
      unwrap(
        await api.POST("/api/v1/assets/certificates/{certificate_id}/revoke", {
          params: { path: { certificate_id: id } },
          body: { reason },
        }),
      ),
    onSuccess: () => invalidateAsset(qc, assetId),
  });
}

/** Record a manual position. */
export function useRecordPosition(assetId: string): UseMutationResult<unknown, Error, PositionIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(
        await api.POST("/api/v1/assets/{asset_id}/positions", {
          params: { path: { asset_id: assetId } },
          body,
        }),
      ),
    onSuccess: () => invalidateAsset(qc, assetId),
  });
}

/** Criticality levels. */
export const CRITICALITIES = ["low", "medium", "high"] as const;
